import React, { useEffect, useState } from 'react';
import { array, bool, object } from 'prop-types';

import { connect } from 'react-redux';

import { has, isEmpty, toNumber, uniq } from 'lodash';

import MaterialTable from 'material-table';
import { Select, MenuItem } from '@material-ui/core';

import ActionBar from '../../../components/ActionBar';
import ActionBarItem from '../../../components/ActionBarItem';
import CustomizeEquipmentModal from '../../Modals/CustomizeEquipmentModal';
import MessageModal from '../../Modals/MessageModal';
import UseExistingEquipmentModal from '../../Modals/UseExistingEquipmentModal';
import CreateEquipmentTableRowMenu from './CreateEquipmentTableRowMenu';

import * as WizardActions from '../../../actions/wizardActions';
import * as CoreActions from '../../../actions/coreActions';

import classes from './ProfileTable.module.css';

const CreateEquipmentTable = ({
    equipmentCreated,
    equipmentTypes,
    initializeTargetDevices,
    isMessageModalOpen,
    setProgressBarLoading,
    targetDevices,
    targetProfile,
    updateTargetDevices,
    ...props
}) => {
    const [customizeEquipmentModalOpen, setCustomizeEquipmentModalOpen] = useState(false);
    const [useExistingEquipmentModalOpen, setUseExistingEquipmentModalOpen] = useState(false);
    const [device, setDevice] = useState();
    const [equipment, setEquipment] = useState([]);
    const [filteredDevices, setFilteredDevices] = useState([]);

    useEffect(() => {
        async function init() {
            setProgressBarLoading(true);
            await initializeTargetDevices();
            setProgressBarLoading(false);
        }

        if (!targetDevices.length) {
            init();
        }
    }, []);

    useEffect(() => {
        setEquipmentTypeName();
        setFilteredDevices(targetDevices);
    }, [targetDevices]);

    const nameSorter = (a, b) => {
        return sorter(a, b, 'name');
    };

    const identifierSorter = (a, b) => {
        if (has(a, 'oBIXPath')) {
            return sorter(a, b, 'oBIXPath');
        }

        const valueA = a.deviceId;
        const valueB = b.deviceId;

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const profileSorter = (a, b) => {
        return sorter(a, b, 'profileName');
    };

    const equipmentTypeSorter = (a, b) => {
        return sorter(a, b, 'equipmentTypeName');
    };

    const existingEquipmentSorter = (a, b) => {
        const valueA = a.useExisting;
        const valueB = b.useExisting;

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const sorter = (a, b, property) => {
        const valueA = a[property].toUpperCase();
        const valueB = b[property].toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const setEquipmentTypeName = () => {
        targetDevices.forEach(device => {
            device.equipmentTypeName = getEquipmentTypeName(device.equipmentType);
        });
    };

    const getEquipmentTypeName = equipmentTypeId => {
        return equipmentTypes.find(type => type.id === equipmentTypeId).name;
    };

    const handleBulkSelection = selections => {
        setEquipment(selections);
    };

    const handleExcludeEquipment = () => {
        equipment.forEach(item => {
            item.isExcluded = true;
            updateTargetDevices(item);
        });
    };

    const handleIncludeEquipment = () => {
        equipment.forEach(item => {
            item.isExcluded = false;
            updateTargetDevices(item);
        });
    };

    const handleSearch = searchTerm => {
        if (isEmpty(searchTerm)) {
            setFilteredDevices(targetDevices);

            return;
        }

        let name,
            identifier,
            profile,
            equipmentTypeNames,
            niagaraDevices = [],
            bacNetDevices = [],
            groupedDevices;

        const normalizeSearchTerm = searchTerm.toLowerCase();
        let convertedSearchTerm;

        if (!isNaN(normalizeSearchTerm)) {
            convertedSearchTerm = toNumber(normalizeSearchTerm);
        }

        name = targetDevices
            .filter(device => !isEmpty(device.name))
            .filter(device => device.name.toLowerCase().includes(normalizeSearchTerm));

        profile = targetDevices
            .filter(device => !isEmpty(device.profileName))
            .filter(device => device.profileName.toLowerCase().includes(normalizeSearchTerm));

        equipmentTypeNames = targetDevices
            .filter(device => device.equipmentTypeName.toLowerCase().includes(normalizeSearchTerm));

        targetDevices.forEach(device => {
            if (device.oBIXPath) {
                niagaraDevices.push(device);
            } else if (device.deviceId) {
                bacNetDevices.push(device);
            }
        });

        if (niagaraDevices.length) {
            identifier = niagaraDevices
                .filter(device => !isEmpty(device.oBIXPath))
                .filter(device => device.oBIXPath.toLowerCase().includes(normalizeSearchTerm));
        } else if (bacNetDevices.length) {
            identifier = bacNetDevices
                .filter(device => {
                    return device.deviceId === convertedSearchTerm;
                });
        }

        groupedDevices = [...name, ...profile, ...equipmentTypeNames, ...identifier];

        setFilteredDevices(uniq(groupedDevices));
    };

    const identifierCustomFilterAndSort = (term, rowData) => {
        if (rowData.oBIXPath) {
            return rowData.oBIXPath.toLowerCase().includes(term.toLowerCase());
        }
        else if (rowData.deviceId) {
            return rowData.deviceId.toString().toLowerCase().includes(term.toLowerCase());
        }

        return;
    };

    const existingEquipmentFilterComponent = ({columnDef, onFilterChanged}) => (
        <Select
            id="enabledSelect"
            style={{width: 100}}
            defaultValue={'all'}
            onChange={(e) => {
                let val;

                switch (e.target.value) {
                    case 'all':
                        val = undefined;
                        break;
                    case 'checked':
                        val = 'checked';
                        break;
                    case 'unchecked':
                        val = 'unchecked';
                        break;
                    default:
                        val = undefined;
                }
        
                onFilterChanged(columnDef.tableData.id, val);                                        
            }}
        >   
            <MenuItem value={'all'}>All</MenuItem>
            <MenuItem value={'checked'}>Yes</MenuItem>
            <MenuItem value={'unchecked'}>No</MenuItem>
        </Select>
    );

    // Define columns prior to injecting them into component's state
    const columns = [
        {
            title:      'Name',
            field:      'name',
            render:     rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.name}</span>,
            customSort: nameSorter
        },
        {
            title:                 'Identifier',
            customFilterAndSearch: identifierCustomFilterAndSort,
            render:                rowData => {
                if (rowData.oBIXPath) return <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.oBIXPath}</span>;
                if (rowData.deviceId) return <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.deviceId}</span>;

                return '';
            },
            customSort: identifierSorter
        },
        {
            title:                 'Profile',
            name:                  'profileName',
            customFilterAndSearch: (term, rowData) => rowData.profileName.toLowerCase().includes(term.toLowerCase()),
            render:                rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.profileName}</span>,
            customSort:            profileSorter
        },
        {
            title:                 'Equipment Type',
            name:                  'equipmentTypeName',
            customFilterAndSearch: (term, rowData) => rowData.equipmentTypeName.toLowerCase().includes(term.toLowerCase()),
            render:                rowData => <span className={rowData.isExcluded ? classes.excluded : ''}>{rowData.equipmentTypeName}</span>,
            customSort:            equipmentTypeSorter
        },
        {
            title:           'Existing Equipment',
            //customFilterAndSearch: (term, rowData) => rowData.useExisting === term,
            filterComponent: existingEquipmentFilterComponent,
            render:          rowData => rowData.useExisting ? <img src="/assets/images/billing-ok.svg" alt="checkmark" /> : null,
            customSort:      existingEquipmentSorter,
            field:           'useExisting',
            type:            'boolean'
        },
        {
            title:     '',
            render:    rowData => (equipmentCreated) ? null : renderMenu(rowData),
            filtering: false
        }
    ];

    // This is a WORK AROUND for a Material Table (as of v1.68) issue where column sort state was lost when parent
    // component's state updates. This technique is not meant for global adoption; check on the current
    // state of Material Table & determine if a solution has been introduced before copy/pasting this code.
    // That said, lifting the columns into the parent's state preserves it and prevents state change from
    // "dropping" the current column sort order.
    //
    // https://github.com/mbrn/material-table/issues/1265
    const [tableColumns] = useState(columns);

    const renderMenu = rowData => {
        return (
            <CreateEquipmentTableRowMenu
                device={rowData}
                setDevice={setDevice}
                updateDevice={updateTargetDevices}
                onCustomizeEquipmentModal={setCustomizeEquipmentModalOpen}
                onUseExistingEquipmentModal={setUseExistingEquipmentModalOpen}
            />
        );
    };

    if (!targetDevices.length) return null;

    return (
        <div className={classes.ProfileTable}>
            <div className={classes.title}>
                <h2>Create Equipment</h2>
                <ActionBar
                    onSearch={handleSearch}
                >
                    <ActionBarItem
                        onClick={handleIncludeEquipment}
                        disabled={!equipment.length}
                        type="include"
                    >
                        Include Equipment
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={handleExcludeEquipment}
                        disabled={!equipment.length}
                        type="exclude"
                    >
                        Exclude Equipment
                    </ActionBarItem>
                </ActionBar>
            </div>
            <div className={classes.tableContainer}>
                <MaterialTable
                    options={{
                        paging:      false,
                        showTitle:   false,
                        search:      false,
                        selection:   true,
                        toolbar:     false,
                        filtering:   true,
                        headerStyle: { position: 'sticky', top: 0 },
                    }}
                    columns={tableColumns}
                    data={filteredDevices}
                    onSelectionChange={handleBulkSelection}
                />
            </div>
            <CustomizeEquipmentModal
                open={customizeEquipmentModalOpen}
                close={() => setCustomizeEquipmentModalOpen(false)}
                device={device}
                title="Customize Equipment"
            />
            <UseExistingEquipmentModal
                open={useExistingEquipmentModalOpen}
                close={() => setUseExistingEquipmentModalOpen(false)}
                device={device}
                title="Use Existing Equipment"
            />
            <MessageModal
                open={isMessageModalOpen}
                title="Save Status"
            />
        </div>
    );
};

CreateEquipmentTable.defaultProps = {
    equipmentCreated:   false,
    equipmentTypes:     [],
    isMessageModalOpen: false,
    targetDevices:      [],
    targetProfile:      {}
};

CreateEquipmentTable.propTypes = {
    equipmentCreated:   bool,
    equipmentTypes:     array,
    isMessageModalOpen: bool,
    targetDevices:      array,
    targetProfile:      object
};

const mapStateToProps = state => {
    return {
        equipmentCreated:   state.wizard.equipmentCreated,
        equipmentTypes:     state.equipmentTypes.data,
        isMessageModalOpen: state.wizard.isMessageModalOpen,
        targetDevices:      state.wizard.targetDevices,
        targetProfile:      state.wizard.targetProfile
    };
};

const mapDispatchToProps = {
    ...CoreActions,
    ...WizardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEquipmentTable);
